import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactModal from "react-modal";
import { useNavigate } from "react-router-dom";

import { Partner } from "@api";
import { Button, Input, Spinner } from "@atoms";
import { Layout } from "@layouts";
import { Item, Card } from "@molecules";
import { ActionBanner } from "@molecules/action-banner";
import { FormikErrors, useFormik } from "formik";
import {
  useGetPartnerUpdatedSeatCount,
  useGetPartnerCheckoutPortalLink,
} from "@queries";
import {
  useRemoveUserToPremiumPartner,
  useEditUserFromPremiumPartner,
  useResendEmailToUserFromPremiumPartner,
} from "@mutations";
import { EmailLanguage, EmployeeFormValues } from "./logic";
import { styles as globalStyles } from "../../../styles/styles";
import { subscriptionPlanData } from "../billing/subscription-data";
import { SubscriptionPlansTypes, Invoice } from "../../../types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input as SInput,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Button as ShadcnButton,
  Badge,
} from "@shadcn";
import { MoreHorizontal, Check, Clock, X, ChevronDown, Users as UsersIcon } from "lucide-react";

const colors = require("@theme/colors") as Record<string, any>;
const ROWS_PER_PAGE = 15;
const DROPDOWN_HIGHLIGHT =
  "hover:!bg-primary-lightest focus:!bg-primary-lightest data-[highlighted]:!bg-primary-lightest hover:!text-primary-medium focus:!text-primary-medium data-[highlighted]:!text-primary-medium";

type ImportPreviewStatus = "ready" | "error" | "skipped";

type ImportPreviewRow = {
  lineNumber: number;
  firstName: string;
  lastName: string;
  email: string;
  employmentStartDate?: string;
  birthday?: string;
  status: ImportPreviewStatus;
  message?: string;
};

export type EmployeeFormValuesEdit = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  coinmilesAccount: string;
  employmentStartDate: string;
  birthday: string;
};

export interface EmployeeEditProps {
  firstName: string;
  lastName: string;
  emailAddress: string;
  coinmilesAccount: string;
  employmentStartDate: string;
  birthday: string;
}

export type TeamPageTemplateProps = {
  premiumUsers: any[];
  onAddTeamMemberClicked: () => void;
  onEditTeamMemberClicked: () => void;
  onManageNumberOfSeatsClicked: () => void;
  onImportTeamCsv: (file: File) => void | Promise<void>;
  onCancelImport: () => void;
  onConfirmImportPreview: () => void;
  closeImportPreview: () => void;
  partner: Partner | null;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  values: EmployeeFormValues;
  errors: FormikErrors<EmployeeFormValues>;
  handleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void;
  modalIsOpen: boolean;
  closeModal: () => void;
  isAddressSubmitted: boolean;
  isCreditCardAdded: boolean;
  creditCardStatus: "valid" | "invalid" | "expired" | "expiringSoon";
  creditCards: any;
  isLoadingData: boolean;
  setIsLoadingData: (isLoading: boolean) => void;
  emailNotAlreadyAdded: boolean;
  isImporting: boolean;
  importCompletedCount: number;
  importTotalCount: number;
  isParsingImport: boolean;
  isImportPreviewOpen: boolean;
  importPreviewRows: ImportPreviewRow[];
  importPreviewFileName: string | null;
  importAutoSignUp: boolean;
  importLanguage: EmailLanguage;
  setImportAutoSignUp: (value: boolean) => void;
  setImportLanguage: (value: EmailLanguage) => void;
  errorModalIsOpen: boolean;
  errorModalMessage: string | null;
  errorModalTitle: string | null;
  closeErrorModal: () => void;
  importResults: {
    lineNumber: number;
    firstName: string;
    lastName: string;
    email: string;
    status: "success" | "duplicate" | "failure";
    message?: string;
  }[];
  importResultsFilter: "success" | "duplicate" | "failure";
  setImportResultsFilter: (filter: "success" | "duplicate" | "failure") => void;
  isImportResultsOpen: boolean;
  setIsImportResultsOpen: (open: boolean) => void;
  exportFailedCsv: () => void;
  lastImportFileName: string | null;
};

export const TeamPageTemplate = ({
  premiumUsers,
  onAddTeamMemberClicked,
  onEditTeamMemberClicked,
  onManageNumberOfSeatsClicked,
  onImportTeamCsv,
  onCancelImport,
  onConfirmImportPreview,
  closeImportPreview,
  partner,
  handleInputChange,
  values,
  errors,
  handleSubmit,
  modalIsOpen,
  closeModal,
  isAddressSubmitted,
  isCreditCardAdded,
  creditCardStatus,
  creditCards,
  isLoadingData,
  setIsLoadingData,
  emailNotAlreadyAdded,
  isImporting,
  importCompletedCount,
  importTotalCount,
  isParsingImport,
  isImportPreviewOpen,
  importPreviewRows,
  importPreviewFileName,
  importAutoSignUp,
  importLanguage,
  setImportAutoSignUp,
  setImportLanguage,
  errorModalIsOpen,
  errorModalMessage,
  errorModalTitle,
  closeErrorModal,
  importResults,
  importResultsFilter,
  setImportResultsFilter,
  isImportResultsOpen,
  setIsImportResultsOpen,
  exportFailedCsv,
  lastImportFileName,
}: TeamPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const [rowIndex, setRowIndex] = useState<number>(-1);
  const [isDeletingEmployeeAttempted, setIsDeletingEmployeeAttempted] =
    useState<boolean>(false);
  const [isEditingEmployeeAttempted, setIsEditingEmployeeAttempted] =
    useState<boolean>(false);
  const [isResendingInvitationAttempted, setIsResendingInvitationAttempted] =
    useState<boolean>(false);
  const [isResendSuccessOpen, setIsResendSuccessOpen] =
    useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const removeUser = useRemoveUserToPremiumPartner();
  const editUser = useEditUserFromPremiumPartner();
  const resendInviteToUser = useResendEmailToUserFromPremiumPartner();
  const [seatCount, setSeatCount] = useState<number | undefined>(undefined);
  const [checkoutLink, setCheckoutLink] = useState<string | undefined>("");
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const employmentHiddenDateRef = useRef<HTMLInputElement | null>(null);
  const successCount = importResults.filter((r) => r.status === "success").length;
  const duplicateCount = importResults.filter((r) => r.status === "duplicate").length;
  const failureCount = importResults.filter((r) => r.status === "failure").length;
  const filteredResults = importResults.filter(
    (r) => r.status === importResultsFilter,
  );
  const birthdayHiddenDateRef = useRef<HTMLInputElement | null>(null);
  const errorReportExtLabel = (() => {
    const ext = lastImportFileName?.split(".").pop()?.toLowerCase();
    if (ext === "xls" || ext === "xlsx") return ".xlsx";
    return ".csv";
  })();

  const triggerImportDialog = () => {
    fileInputRef.current?.click();
  };

  const handleImportFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void onImportTeamCsv(file);
    }
    event.target.value = "";
  };

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(0);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(0);
  };

  const validate = (
    values: EmployeeFormValuesEdit,
  ): FormikErrors<EmployeeFormValuesEdit> => {
    const errors: FormikErrors<EmployeeFormValuesEdit> = {};
    /* if (!values.emailAddress) {
        errors.emailAddress = "Email is required";
      } else if (
        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.emailAddress)
      ) {
        errors.emailAddress = "Invalid email address";
      } */
    return errors;
  };

  const {
    values: editValues,
    errors: editErrors,
    handleChange,
    handleSubmit: handleEditSubmit,
    setFieldError,
    setFieldValue,
  } = useFormik<EmployeeFormValuesEdit>({
    initialValues: {
      firstName: "",
      lastName: "",
      emailAddress: "",
      coinmilesAccount: "",
      employmentStartDate: "",
      birthday: "",
    },
    validate,
    validateOnChange: false,
    onSubmit: async (values: EmployeeEditProps) => {
      setIsLoadingData(true);
      try {
        console.log("Attempted editing");
        await editUser(values.firstName, values.lastName, rowIndex ?? -1, values.employmentStartDate, values.birthday);
        return;
      } catch (error: any) {
        console.log(error);
        // use setFieldErrors here or nuke that stuff
      }
    },
  });

  const onClickeResendInvitation = (rowIndex: number) => {
    setRowIndex(rowIndex);
    setIsResendingInvitationAttempted(true);
  };
  const onClickEdit = (rowIndex: number) => {
    setRowIndex(rowIndex);
    setIsEditingEmployeeAttempted(true);
    
    console.log("Editing user data:", premiumUsers[rowIndex]);
    console.log("Raw employmentStartDate:", premiumUsers[rowIndex].employmentStartDate, "type:", typeof premiumUsers[rowIndex].employmentStartDate);
    console.log("Raw birthday:", premiumUsers[rowIndex].birthday, "type:", typeof premiumUsers[rowIndex].birthday);
    
    // Use setFieldValue to properly update Formik form state
    setFieldValue("firstName", premiumUsers[rowIndex].firstName || "");
    setFieldValue("lastName", premiumUsers[rowIndex].lastName || "");
    
    // Handle date fields properly - convert to ISO string format for date inputs
    let employmentStartDate = "";
    let birthday = "";
    
    if (premiumUsers[rowIndex].employmentStartDate) {
      try {
        const date = new Date(premiumUsers[rowIndex].employmentStartDate);
        if (!isNaN(date.getTime())) {
          [employmentStartDate] = date.toISOString().split('T');
        }
      } catch (error) {
        console.error("Error parsing employmentStartDate:", error);
      }
    }
    
    if (premiumUsers[rowIndex].birthday) {
      try {
        const date = new Date(premiumUsers[rowIndex].birthday);
        if (!isNaN(date.getTime())) {
          [birthday] = date.toISOString().split('T');
        }
      } catch (error) {
        console.error("Error parsing birthday:", error);
      }
    }
    
    console.log("Converted dates - employmentStartDate:", employmentStartDate, "birthday:", birthday);
    
    setFieldValue("employmentStartDate", employmentStartDate);
    setFieldValue("birthday", birthday);
  };

  const onClickDelete = (rowIndex: number) => {
    setRowIndex(rowIndex);
    setIsDeletingEmployeeAttempted(true);
  };

  const filteredUsers = premiumUsers
    .map((user, index) => ({ user, index }))
    .filter(({ user }) => {
      const query = searchTerm.trim().toLowerCase();
      if (!query) return true;
      const email = (user.emailAddress ?? "").toLowerCase();
      const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.toLowerCase();
      return email.includes(query) || name.includes(query);
    });
  const totalPages = Math.ceil(filteredUsers.length / ROWS_PER_PAGE);
  const effectivePage = totalPages > 0 ? Math.min(currentPage, totalPages - 1) : 0;
  const startIndex = effectivePage * ROWS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + ROWS_PER_PAGE);
  const canGoPrevious = effectivePage > 0;
  const canGoNext = totalPages > 0 && effectivePage < totalPages - 1;
  const showingFrom = filteredUsers.length ? startIndex + 1 : 0;
  const showingTo = filteredUsers.length ? startIndex + paginatedUsers.length : 0;

  const formatDate = (value: string | Date | null | undefined) => {
    if (!value) return "--";

    const buildDateFromParts = (raw: string) => {
      const datePart = raw.includes("T") ? raw.split("T")[0] : raw;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;
      const [year, month, day] = datePart.split("-").map(Number);
      return new Date(year, Math.max(0, (month || 1) - 1), day || 1);
    };

    let date: Date | null = null;

    if (value instanceof Date) {
      date = value;
    } else if (typeof value === "string") {
      date = buildDateFromParts(value.trim());
      if (!date) {
        const parsed = new Date(value);
        date = Number.isNaN(parsed.getTime()) ? null : parsed;
      }
    } else {
      const parsed = new Date(value);
      date = Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (!date || Number.isNaN(date.getTime())) {
      return "--";
    }

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const employmentStartRaw = values.employmentStartDate;
  const birthdayRaw = values.birthday;

  const employmentStartDisplay =
    formatDate(employmentStartRaw) !== "--" ? formatDate(employmentStartRaw) : "";

  const birthdayDisplay =
    formatDate(birthdayRaw) !== "--" ? formatDate(birthdayRaw) : "";

  const datePlaceholder = "Jan 4, 2026";

  function closeDeleteModal() {
    setIsDeletingEmployeeAttempted(false);
  }

  function closeEditModal() {
    setIsEditingEmployeeAttempted(false);
  }

  function closeResendModal() {
    setIsResendingInvitationAttempted(false);
  }

  function closeResendSuccessModal() {
    setIsResendSuccessOpen(false);
  }

  const onConfirmResendEmailInvitation = () => {
    setIsLoadingData(true);
    resendInviteToUser(rowIndex)
      .then(() => {
        setIsResendingInvitationAttempted(false);
        setIsResendSuccessOpen(true);
      })
      .catch(() => {
        // allow onError handler inside mutation hook to handle state
      });
  }

  const onConfirmUserDelete = () => {
    /* Delete email from list of users */
    setIsLoadingData(true);
    removeUser(premiumUsers?.[rowIndex]?.emailAddress);
    // closeModal();
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    handleChange(e);

  const handleNextPage = () => {
    if (!canGoNext) return;
    setCurrentPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (!canGoPrevious) return;
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

    useEffect(() => {
      if (!isLoadingData) closeResendModal();
    }, [isLoadingData]);
  useEffect(() => {
    if (!isLoadingData) closeEditModal();
  }, [isLoadingData]);

  useEffect(() => {
    if (!isLoadingData) closeDeleteModal();
  }, [isLoadingData]);
  useEffect(() => {
    const computedTotalPages = Math.ceil(filteredUsers.length / ROWS_PER_PAGE);
    if (currentPage > 0 && currentPage >= computedTotalPages) {
      setCurrentPage(Math.max(computedTotalPages - 1, 0));
    }
  }, [filteredUsers.length, currentPage]);

  const { birthday, emailAddress, language, employmentStartDate, firstName, lastName, autoSignUp } =
    values;
  const {
    birthday: birthdayError,
    emailAddress: emailAddressError,
    employmentStartDate: employmentStartDateError,
    firstName: firstNameError,
    lastName: lastNameError,
  } = errors;

  const { getPartnerUpdatedSeatCount, partnerUpdatedSeatCount } =
    useGetPartnerUpdatedSeatCount();

  // const { getPartnerCheckoutPortalLink, partnerCheckoutPortalLink } = useGetPartnerCheckoutPortalLink();

  useEffect(() => {
    // getPartnerUpdatedSeatCount();
    // getPartnerCheckoutPortalLink();
  }, []);

  // useEffect(() => {
  //   setCheckoutLink(partnerCheckoutPortalLink);
  // }, [partnerCheckoutPortalLink])

  useEffect(() => {
    setSeatCount(partnerUpdatedSeatCount);
  }, [partnerUpdatedSeatCount]);

  // Debug: Monitor editValues changes
  useEffect(() => {
    if (isEditingEmployeeAttempted) {
      console.log("editValues updated:", editValues);
    }
  }, [editValues, isEditingEmployeeAttempted]);

  const hasSeatsAvailable = premiumUsers.length > 0;
  const isCreditCardExpired = creditCardStatus === "expired";
  const shouldDisableImportCsv = isAddressSubmitted && (!isCreditCardAdded || isCreditCardExpired);
  const readyImportCount = importPreviewRows.filter(
    (row) => row.status === "ready",
  ).length;
  const previewIssuesCount = importPreviewRows.length - readyImportCount;
  const invalidEntriesColor =
    previewIssuesCount > 0 ? colors.red : colors.neutral.mediumDark;
  const baseModalContent =
    (globalStyles.generalModal && globalStyles.generalModal.content) || {};
  const originalWidth =
    typeof baseModalContent.width === "number" ? baseModalContent.width : 400;
  const originalHeight =
    typeof baseModalContent.height === "number" ? baseModalContent.height : 320;
  const originalMaxHeight =
    typeof baseModalContent.maxHeight === "number"
      ? baseModalContent.maxHeight
      : undefined;
  const previewModalStyle = {
    content: {
      ...baseModalContent,
      width: 'calc(100vw - 48px)',
      maxWidth: originalWidth * 3,
      height: undefined,
      minHeight: undefined,
      maxHeight: (originalMaxHeight ?? originalHeight) * 2 + 15,
    },
    overlay:
      (globalStyles.generalModal && globalStyles.generalModal.overlay) || {},
  };
  const resultsModalStyle = {
    content: {
      ...previewModalStyle.content,
      width: 'calc((100vw - 48px) * 0.75)',
      maxWidth: (originalWidth * 3) * 0.75,
    },
    overlay: previewModalStyle.overlay,
  };

  const downloadImportTemplate = () => {
    if (typeof document === "undefined") {
      return;
    }
    const link = document.createElement("a");
    link.href = "/team-import-template.csv";
    link.download = "parachute-team-import-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadImportTemplateXlsx = () => {
    if (typeof document === "undefined") {
      return;
    }
    const link = document.createElement("a");
    link.href = "/team-import-template.xlsx";
    link.download = "parachute-team-import-template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const ImportMenuButton = ({ fullWidth = false }: { fullWidth?: boolean }) => {
    const buttonClasses = [
      "flex items-center justify-center gap-2 rounded-full border px-5 py-2 ml-3 text-sm font-semibold shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-medium focus-visible:ring-offset-2",
      fullWidth ? "w-full" : "",
      shouldDisableImportCsv ? "pointer-events-none" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const buttonStyle = {
      backgroundColor: shouldDisableImportCsv ? colors.neutral.light : "#FFFFFF",
      color: shouldDisableImportCsv ? "#FFFFFF" : colors.primary.medium,
      borderColor: shouldDisableImportCsv ? colors.neutral.light : colors.primary.medium,
    };

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
          type="button"
          disabled={shouldDisableImportCsv}
          aria-disabled={shouldDisableImportCsv}
          className={buttonClasses}
          style={buttonStyle}
        >
          {t("teamPage.import.importTitle")}
          <ChevronDown className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 team-actions-menu">
          <DropdownMenuLabel>{t("teamPage.import.menuLabel")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={shouldDisableImportCsv}
            onSelect={() => {
              if (!shouldDisableImportCsv) {
                triggerImportDialog();
              }
            }}
          >
            {t("teamPage.import.importFile")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={downloadImportTemplate}>
            {t("teamPage.import.downloadTemplate")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={downloadImportTemplateXlsx}>
            {t("teamPage.import.downloadTemplateXlsx")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <Layout
      variant="dashboard"
      header={t("teamPage.header")}
      subheader={
        <div className="page-subtitle mt-2">
          {t("teamPage.subheader")}
        </div>
      }
    >
      <div className="max-w-5xl">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xls,.xlsx"
          onChange={handleImportFileChange}
          className="hidden"
        />
        <div className="pt-4 px-0">
          {!isAddressSubmitted && (
            <div className="pb-6">
              <div className="text-attention-darkest mt-2 p-3 pl-6 rounded-xl bg-attention-lightest w-1/2">
                {t("teamPage.addressAlert")}
              </div>
            </div>
          )}
        {isAddressSubmitted && !isCreditCardAdded && (
          <ActionBanner
            text={t("teamPage.addCardAlert")}
            onClick={() => navigate("/dashboard/onboarding-add-card")}
          />
        )}
        {creditCardStatus === "expired" && (
          <ActionBanner
            text="🚨 Your credit card has expired. Click here to update your info."
            onClick={() => navigate("/dashboard/billing")}
          />
        )}
        {creditCardStatus === "expiringSoon" && (
          <ActionBanner
            text="⚠️ Your credit card is expiring soon. Visit the billing page to update it."
            onClick={() => navigate("/dashboard/billing")}
          />
        )}
        </div>

        <div className="p-0 mr-4 rounded-md w-full max-w-5xl">
          <div className="rounded-2xl border border-neutral-light bg-white p-6">
            {/* Below we'll need an if-else statement to either display employees or display smth to say no employees are added */}
            {premiumUsers.length ? (
              <div className="space-y-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between pb-2">
                <div className="flex w-full md:w-auto md:flex-1 items-center">
                  <div className="text-lg font-semibold text-neutral-dark flex-shrink-0 pr-6">
                    {t("teamPage.membersCount", { count: premiumUsers.length })}
                  </div>
                  <div className="relative w-full md:w-[360px]">
                    <SInput
                      placeholder={t("teamPage.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchInputChange}
                      className="w-full pr-10 bg-white input-focus-primary"
                    />
                    {searchTerm ? (
                      <button
                        type="button"
                        onClick={handleClearSearch}
                        className="absolute inset-y-0 right-3 flex items-center text-neutral-medium hover:text-neutral-dark focus:outline-none"
                        aria-label={t("teamPage.actions.clearSearch")}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <ImportMenuButton />
                  <div className="w-56">
                    <Button
                      onClick={
                        isAddressSubmitted && creditCards?.length > 0 && !isCreditCardExpired
                          ? onAddTeamMemberClicked
                          : undefined
                      }
                      variant="primary"
                      label={t("teamPage.addMember")}
                      stretch
                      textWeight={"semibold"}
                      color={
                        isAddressSubmitted && creditCards?.length > 0 && !isCreditCardExpired
                          ? "brand-orange"
                          : "neutral-light"
                      }
                      textColor="white"
                      hoverColor={
                        isAddressSubmitted && creditCards?.length > 0 && !isCreditCardExpired
                          ? "primary-dark"
                          : "neutral-light"
                      }
                      loading={isAddressSubmitted && creditCards === null}
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-neutral-light overflow-hidden">
                <Table className="border-collapse">
                    <TableHeader className="table-header-background">
                      <TableRow className="border-b border-neutral-light">
                        <TableHead className="pl-[10px] px-4 text-xs font-medium table-header-text uppercase tracking-wider">
                        {t("teamPage.tableHeaders.name")}
                        </TableHead>
                        <TableHead className="text-xs px-4 font-medium table-header-text uppercase tracking-wider">
                        {t("teamPage.tableHeaders.email")}
                        </TableHead>
                        <TableHead className="text-xs px-4 font-medium table-header-text uppercase tracking-wider">
                        {t("teamPage.tableHeaders.workAnniversary")}
                        </TableHead>
                        <TableHead className="text-xs px-4 font-medium table-header-text uppercase tracking-wider">
                        {t("teamPage.tableHeaders.birthday")}
                        </TableHead>
                        <TableHead className="text-xs px-4 font-medium table-header-text uppercase tracking-wider">
                        {t("teamPage.tableHeaders.status")}
                        </TableHead>
                        <TableHead className="pr-[10px] px-4 text-xs font-medium table-header-text uppercase tracking-wider text-right">
                        {t("teamPage.tableHeaders.actions")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                  <TableBody>
                    {filteredUsers.length ? (
                      paginatedUsers.map(({ user, index }, pageIdx) => {
                        const isInProgress = !user.userId;
                        const isLastRow = pageIdx === paginatedUsers.length - 1;
                        const statusConfig = isInProgress
                          ? {
                              label: t("teamPage.status.inProgress"),
                              badgeClass:
                                "bg-white border-neutral-light text-neutral-dark",
                              iconWrapperClass: "bg-neutral-light text-neutral-dark h-4 w-4",
                              Icon: Clock,
                            }
                          : {
                              label: t("teamPage.status.onboarded"),
                              badgeClass: "bg-white border-green-200 text-green-700",
                              iconWrapperClass: "bg-green-500 text-white h-3 w-3",
                              Icon: Check,
                            };

                        return (
                          <TableRow key={`${user.emailAddress}-${index}`} className="border-b-0">
                            <TableCell className={`pl-[10px] px-4 text-sm font-medium text-neutral-darkest ${isLastRow ? "" : "border-b border-neutral-light"}`}>
                              {`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "--"}
                            </TableCell>
                            <TableCell className={`text-xs px-4 text-neutral-dark ${isLastRow ? "" : "border-b border-neutral-light"}`}>
                              {user.emailAddress || "--"}
                            </TableCell>
                            <TableCell className={`text-xs px-4 text-neutral-dark ${isLastRow ? "" : "border-b border-neutral-light"}`}>
                              {formatDate(user.employmentStartDate)}
                            </TableCell>
                            <TableCell className={`text-xs px-4 text-neutral-dark ${isLastRow ? "" : "border-b border-neutral-light"}`}>
                              {formatDate(user.birthday)}
                            </TableCell>
                            <TableCell className={`px-4 ${isLastRow ? "" : "border-b border-neutral-light"}`}>
                              <Badge
                                variant="outline"
                                className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium ${statusConfig.badgeClass}`}
                              >
                                <span
                                  className={`flex items-center justify-center rounded-full ${statusConfig.iconWrapperClass}`}
                                >
                                  <statusConfig.Icon
                                    className={isInProgress ? "h-2.5 w-2.5" : "h-1.5 w-1.5"}
                                  />
                                </span>
                                <span>{statusConfig.label}</span>
                              </Badge>
                            </TableCell>
                            <TableCell className={`pr-[10px] px-4 text-right ${isLastRow ? "" : "border-b border-neutral-light"}`}>
                                  <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <ShadcnButton
                                    variant="ghost"
                                    className="h-8 w-10 p-0 border-0 outline-none ring-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                  >
                                    <span className="sr-only">{t("teamPage.tableHeaders.actions")}</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </ShadcnButton>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="min-w-[12rem] whitespace-nowrap team-actions-menu"
                                >
                                  <DropdownMenuLabel>{t("teamPage.tableHeaders.actions")}</DropdownMenuLabel>
                                  <DropdownMenuSeparator className={"text-black"} />
                                  {isInProgress ? (
                                  <DropdownMenuItem
                                    onClick={() => onClickeResendInvitation(index)}
                                    className={`${DROPDOWN_HIGHLIGHT} whitespace-nowrap`}
                                  >
                                    {t("teamPage.modals.resendTitle")}
                                  </DropdownMenuItem>
                                  ) : null}
                                  <DropdownMenuItem
                                    onClick={() => onClickEdit(index)}
                                    className={`${DROPDOWN_HIGHLIGHT} whitespace-nowrap`}
                                  >
                                    {t("teamPage.actions.edit", "Edit")}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => onClickDelete(index)}
                                    className={`text-red-600 whitespace-nowrap ${DROPDOWN_HIGHLIGHT}`}
                                  >
                                    {t("teamPage.actions.remove", "Remove")}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="px-[10px] h-24 text-center text-sm text-neutral-medium"
                        >
                          {t("teamPage.emptySearch")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
                {filteredUsers.length > ROWS_PER_PAGE ? (
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-4">
                    <span className="text-xs text-neutral-mediumDark">
                      {t("teamPage.pagination.showing")} {showingFrom}-{showingTo} {t("teamPage.pagination.of")} {filteredUsers.length}
                    </span>
                    <div className="flex gap-2 justify-end">
                      <ShadcnButton
                        variant="outline"
                        size="sm"
                        onClick={handlePreviousPage}
                        disabled={!canGoPrevious}
                        className="px-4"
                      >
                        {t("teamPage.pagination.previous")}
                      </ShadcnButton>
                      <ShadcnButton
                        variant="outline"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={!canGoNext}
                        className="px-4"
                      >
                        {t("teamPage.pagination.next")}
                      </ShadcnButton>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="w-full">
                {/* Empty state header with aligned text and button */}
                <div className="flex flex-row justify-between items-center mb-6">
                  <div className="text-neutral-dark text-base">
                    {t("teamPage.emptyTitle")}
                  </div>
                  <div className="flex items-center gap-3">
                    <ImportMenuButton />
                    <div className="w-56">
                      <Button
                        onClick={
                          isAddressSubmitted && creditCards?.length > 0 && !isCreditCardExpired
                            ? onAddTeamMemberClicked
                            : undefined
                        }
                        variant="primary"
                        label={t("teamPage.addMember")}
                        stretch
                        textWeight={"semibold"}
                        color={
                          isAddressSubmitted && creditCards?.length > 0 && !isCreditCardExpired
                            ? "brand-orange"
                            : "neutral-light"
                        }
                        textColor="white"
                        hoverColor={
                          isAddressSubmitted && creditCards?.length > 0 && !isCreditCardExpired
                            ? "primary-dark"
                            : "neutral-light"
                        }
                        loading={isAddressSubmitted && creditCards === null}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Empty table with design */}
                <div className="border border-neutral-light rounded-lg overflow-hidden">
                  {/* Table header */}
                  <div className="bg-neutral-lightest px-6 py-4 border-b border-neutral-light">
                    <div className="grid grid-cols-5 gap-2 text-sm font-semibold text-neutral-darkest">
                      <div>{t("teamPage.tableHeaders.name")}</div>
                      <div>{t("teamPage.tableHeaders.email")}</div>
                      <div>{t("teamPage.tableHeaders.workAnniversary")}</div>
                      <div>{t("teamPage.tableHeaders.birthday")}</div>
                      <div>{t("teamPage.tableHeaders.status")}</div>
                    </div>
                  </div>
                  
                  {/* Empty state body */}
                  <div className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center">
                      <UsersIcon className="h-10 w-10 mb-3 text-gray-300" />
                      
                      {/* Text */}
                      <h3 className="text-lg font-semibold text-neutral-darkest mb-2">
                        {t("teamPage.emptyTitle")}
                      </h3>
                      <p className="text-neutral-dark max-w-md">
                        {t("teamPage.emptyDescription")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {modalIsOpen ? (
          <ReactModal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            style={globalStyles.generalModal}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
            contentLabel={t("teamPage.modals.addTitle")}
          >
            <div className="font-semibold text-2xl flex flex-row justify-center py-2">
              {t("teamPage.modals.addTitle")}
            </div>
            <form className="p-2" onSubmit={handleSubmit}>
              <div className="mb-3">
                <Input
                  name="firstName"
                  variant="text"
                  onChange={handleInputChange}
                  placeholder={t("teamPage.modals.labels.firstName")}
                  value={firstName}
                  errorMessage={firstNameError}
                />
              </div>
              <div className="mb-3">
                <Input
                  name="lastName"
                  variant="text"
                  onChange={handleInputChange}
                  placeholder={t("teamPage.modals.labels.lastName")}
                  value={lastName}
                  errorMessage={lastNameError}
                />
              </div>
              <div className="mb-0">
                <Input
                  name="emailAddress"
                  variant="text"
                  onChange={handleInputChange}
                  placeholder={t("teamPage.modals.labels.email")}
                  value={emailAddress}
                  errorMessage={emailAddressError}
                />
              </div>
              <div className="mb-3 pt-3">
                <div className="text-xs text-gray-500">{t("teamPage.modals.labels.emailLanguage")}</div>
                <select
                  name="language"
                  value={language}
                  onChange={handleInputChange}
                  className="w-full h-12 rounded-sm bg-white input-focus-primary text-left px-3 text-sm border border-neutral-light text-neutral-dark"
                >
                  <option value="en">{t("teamPage.modals.labels.languageEnglish")}</option>
                  <option value="fr">{t("teamPage.modals.labels.languageFrench")}</option>
                </select>
              </div>
              <div className="mb-3 py-2">
                <div className="h-px bg-neutral-light mb-3" />
                <div className="text-xs text-gray-500 pt-3">
                  {t("teamPage.modals.labels.workAnniversary")}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      const el = employmentHiddenDateRef.current;
                      if (el?.showPicker) {
                        el.showPicker();
                      } else {
                        el?.click();
                      }
                    }}
                    className="w-full h-12 rounded-sm bg-white input-focus-primary text-left px-3 text-sm border border-neutral-light"
                  >
                    <span className={employmentStartDisplay ? "text-neutral-dark" : "text-neutral-medium"}>
                      {employmentStartDisplay || t("teamPage.modals.labels.selectDate")}
                    </span>
                  </button>
                  <input
                    ref={employmentHiddenDateRef}
                    name="employmentStartDate"
                    type="date"
                    value={employmentStartRaw || ""}
                    onChange={handleInputChange}
                    className="absolute inset-0 opacity-0 pointer-events-none h-0 w-0"
                    tabIndex={-1}
                  />
                </div>
              </div>
              <div className="mb-3">
                <div className="text-xs text-gray-500">{t("teamPage.modals.labels.birthday")}</div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      const el = birthdayHiddenDateRef.current;
                      if (el?.showPicker) {
                        el.showPicker();
                      } else {
                        el?.click();
                      }
                    }}
                    className="w-full h-12 rounded-sm bg-white input-focus-primary text-left px-3 text-sm border border-neutral-light"
                  >
                    <span className={birthdayDisplay ? "text-neutral-dark" : "text-neutral-medium"}>
                      {birthdayDisplay || t("teamPage.modals.labels.selectDate")}
                    </span>
                  </button>
                  <input
                    ref={birthdayHiddenDateRef}
                    name="birthday"
                    type="date"
                    value={birthdayRaw || ""}
                    onChange={handleInputChange}
                    className="absolute inset-0 opacity-0 pointer-events-none h-0 w-0"
                    tabIndex={-1}
                  />
                </div>
              </div>
              <div className="mb-4 pt-3">
                <div className="h-px bg-neutral-light mb-5" />
                <div className="flex flex-col space-y-1">
                  <label
                    htmlFor="autoSignUp"
                    className="flex items-center space-x-2 text-sm text-neutral-dark"
                  >
                    <input
                      id="autoSignUp"
                      type="checkbox"
                      name="autoSignUp"
                      checked={autoSignUp}
                      onChange={handleInputChange}
                      className="h-4 w-4 accent-primary-medium"
                    />
                    <span>{t("teamPage.modals.labels.autoSignUp")}</span>
                  </label>
                  <p className="text-xs text-neutral-dark pl-6">
                    {t("teamPage.modals.labels.autoSignUpHelper")}
                  </p>
                </div>
              </div>
              <div className="flex flex-row justify-center">
                <div className="w-64 pt-4">
                  <Button
                    onClick={isLoadingData ? () => {} : handleSubmit}
                    variant="primary"
                    label={t("teamPage.modals.labels.sendInvite")}
                    stretch
                    color={"primary-medium"}
                    brightness={600}
                    textWeight={"semibold"}
                    loading={isLoadingData && emailNotAlreadyAdded}
                  />
                </div>
              </div>
            </form>
          </ReactModal>
        ) : null}
        {isEditingEmployeeAttempted ? (
          <ReactModal
            key={`edit-modal-${rowIndex}`}
            isOpen={isEditingEmployeeAttempted}
            onRequestClose={closeEditModal}
            style={globalStyles.generalModal}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
            contentLabel={t("teamPage.modals.editTitle")}
          >
            <div className="font-semibold text-2xl flex flex-row justify-center py-2">
              {t("teamPage.modals.editTitle")}
            </div>
            <form className="p-2" onSubmit={handleEditSubmit}>
              <div className="mb-3">
                <Input
                  name="firstName"
                  variant="text"
                  onChange={handleEditInputChange}
                  placeholder={t("teamPage.modals.labels.firstName")}
                  value={editValues.firstName}
                  errorMessage={editErrors.firstName}
                />
              </div>
              <div className="mb-3">
                <Input
                  name="lastName"
                  variant="text"
                  onChange={handleEditInputChange}
                  placeholder={t("teamPage.modals.labels.lastName")}
                  value={editValues.lastName}
                  errorMessage={editErrors.lastName}
                />
              </div>
              <div className="mb-3">
                <div className="text-xs text-gray-500">
                  {t("teamPage.modals.labels.workAnniversary")}
                </div>
                <SInput
                  name="employmentStartDate"
                  type="date"
                  onChange={handleEditInputChange}
                  placeholder={t("teamPage.modals.labels.workAnniversary")}
                  value={editValues.employmentStartDate}
                  className="w-full h-12 rounded-sm bg-white input-focus-primary"
                  //errorMessage={birthdayError}
                />
              </div>
              <div className="mb-3">
                <div className="text-xs text-gray-500">{t("teamPage.modals.labels.birthday")}</div>
                <SInput
                  name="birthday"
                  type="date"
                  onChange={handleEditInputChange}
                  placeholder={t("teamPage.modals.labels.birthday")}
                  value={editValues.birthday}
                  className="w-full h-12 rounded-sm bg-white input-focus-primary"
                  //errorMessage={birthdayError}
                />
              </div>
              {/* <div className="mb-3">
              <Input
                name="emailAddress"
                variant="text"
                onChange={handleInputChange}
                placeholder="Email address"
                value={emailAddress}
                errorMessage={errors.emailAddress}
              />
            </div> */}
              <div className="flex flex-row justify-center">
                <div className="w-64">
                  <Button
                    onClick={isLoadingData ? () => {} : handleEditSubmit}
                    variant="primary"
                    label={t("teamPage.modals.labels.updateMember")}
                    stretch
                    color="primary-medium"
                    textWeight={"semibold"}
                    loading={isLoadingData}
                  />
                </div>
              </div>
            </form>
          </ReactModal>
        ) : null}
        {isDeletingEmployeeAttempted ? (
          <ReactModal
            isOpen={isDeletingEmployeeAttempted}
            onRequestClose={closeDeleteModal}
            style={globalStyles.generalModal}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
          >
            <div className="font-semibold text-2xl flex flex-row justify-center py-4">
              {t("teamPage.modals.deleteTitle")}
            </div>
            <div className="mb-5 flex flex-row justify-center">
              {t("teamPage.modals.deleteMessage", {
                name: `${premiumUsers?.[rowIndex]?.firstName ?? ""} ${premiumUsers?.[rowIndex]?.lastName ?? ""}`.trim(),
              })}
            </div>
            <div className="flex flex-row justify-center">
              <div className="w-64">
                <Button
                  onClick={isLoadingData ? () => {} : onConfirmUserDelete}
                  variant="primary"
                  label={t("teamPage.modals.labels.confirm")}
                  stretch
                  textWeight={"semibold"}
                  color="primary-medium"
                  loading={isLoadingData}
                />
              </div>
            </div>
          </ReactModal>
        ) : null}
        {isResendingInvitationAttempted ? (
          <ReactModal
            isOpen={isResendingInvitationAttempted}
            onRequestClose={closeResendModal}
            style={globalStyles.generalModal}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
          >
            <div className="font-semibold text-2xl flex flex-row justify-center py-4">
              {t("teamPage.modals.resendTitle")}
            </div>
            <div className="mb-5 flex flex-row justify-center px-4 text-center">
              {t("teamPage.modals.resendMessage", {
                name: `${premiumUsers?.[rowIndex]?.firstName ?? ""} ${premiumUsers?.[rowIndex]?.lastName ?? ""}`.trim(),
              })}
            </div>
            <div className="flex flex-row justify-center">
              <div className="w-64">
                <Button
                  onClick={isLoadingData ? () => {} : onConfirmResendEmailInvitation}
                  variant="primary"
                  label={t("teamPage.modals.labels.confirm")}
                  stretch
                  textWeight={"semibold"}
                  color="primary-medium"
                  loading={isLoadingData}
                />
              </div>
            </div>
          </ReactModal>
        ) : null}
        {isResendSuccessOpen ? (
          <ReactModal
            isOpen={isResendSuccessOpen}
            onRequestClose={closeResendSuccessModal}
            style={globalStyles.generalModal}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
          >
            <div className="font-semibold text-2xl flex flex-row justify-center py-4">
              {t("teamPage.modals.resendSuccessTitle", "Invitation sent")}
            </div>
            <div className="mb-5 flex flex-row justify-center px-4 text-center">
              {t("teamPage.modals.resendSuccessMessage", {
                name: `${premiumUsers?.[rowIndex]?.firstName ?? ""} ${premiumUsers?.[rowIndex]?.lastName ?? ""}`.trim(),
                defaultValue: "Invitation email sent to {{name}}.",
              })}
            </div>
            <div className="flex flex-row justify-center">
              <div className="w-64">
                <Button
                  onClick={closeResendSuccessModal}
                  variant="primary"
                  label={t("teamPage.modals.labels.ok", "Ok")}
                  stretch
                  textWeight={"semibold"}
                  color="primary-medium"
                />
              </div>
            </div>
          </ReactModal>
        ) : null}
        {isImportPreviewOpen ? (
          <ReactModal
            isOpen={isImportPreviewOpen}
            onRequestClose={closeImportPreview}
            style={previewModalStyle}
            shouldCloseOnOverlayClick={!isParsingImport && !isImporting}
            shouldCloseOnEsc={!isParsingImport && !isImporting}
            ariaHideApp={false}
            contentLabel={t("teamPage.modals.importPreviewTitle")}
          >
            <div className="flex flex-col gap-2 py-4 px-4">
              <div className="text-2xl font-semibold text-center text-neutral-darkest">
                {t("teamPage.modals.importPreviewTitle")}
              </div>
              {importPreviewFileName ? (
                <div className="text-sm text-neutral-dark text-center pb-4">
                  {importPreviewFileName}
                </div>
              ) : null}
              {isParsingImport ? (
                <div className="flex flex-col items-center justify-center gap-3 py-8">
                  <Spinner variant="large" />
                  <div className="text-sm text-neutral-dark">
                    {t("teamPage.modals.importPreviewLoading")}
                  </div>
                </div>
              ) : importPreviewRows.length ? (
                <>
                  <div className="h-full overflow-y-auto border border-neutral-light rounded-lg">
                    <Table>
                      <TableHeader className="table-header-background">
                        <TableRow>
                        <TableHead className="text-xs px-4 font-medium table-header-text uppercase tracking-wider">
                          {t("teamPage.tableHeaders.name")}
                        </TableHead>
                        <TableHead className="text-xs px-4 font-medium table-header-text uppercase tracking-wider">
                          {t("teamPage.tableHeaders.email")}
                        </TableHead>
                        <TableHead className="text-xs px-4 font-medium table-header-text uppercase tracking-wider">
                          {t("teamPage.tableHeaders.workAnniversary")}
                        </TableHead>
                        <TableHead className="text-xs px-4 font-medium table-header-text uppercase tracking-wider">
                          {t("teamPage.tableHeaders.birthday")}
                        </TableHead>
                        <TableHead className="text-xs px-4 font-medium table-header-text uppercase tracking-wider">
                          {t("teamPage.tableHeaders.status")}
                        </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importPreviewRows.map((row) => {
                          const isReady = row.status === "ready";
                          const rowStyle =
                            isReady
                              ? undefined
                              : { backgroundColor: colors.attention.lightest };
                          return (
                            <TableRow
                              key={`${row.lineNumber}-${row.email}`}
                              style={rowStyle}
                            >
                              <TableCell className="text-sm px-4 text-neutral-dark">
                                {`${row.firstName ?? ""} ${row.lastName ?? ""}`.trim() ||
                                  "--"}
                              </TableCell>
                              <TableCell className="text-sm px-4 text-neutral-dark">
                                {row.email || "--"}
                              </TableCell>
                              <TableCell className="text-sm px-4 text-neutral-dark">
                                {formatDate(row.employmentStartDate)}
                              </TableCell>
                              <TableCell className="text-sm px-4 text-neutral-dark">
                                {formatDate(row.birthday)}
                              </TableCell>
                              <TableCell className="text-sm px-4">
                                {isReady ? (
                                  <Badge
                                    variant="outline"
                                    className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium bg-white border-green-200 text-green-700"
                                  >
                                    <span className="flex items-center justify-center rounded-full bg-green-500 text-white h-3 w-3">
                                      <Check className="h-1.5 w-1.5" />
                                    </span>
                                    <span>{t("teamPage.importPreview.statusReady")}</span>
                                  </Badge>
                                ) : (
                                  <span className="text-red-600">
                                    {row.message || t("teamPage.importPreview.statusNeedsReview")}
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                    <div className="space-y-2 w-full pt-4">
                      <div className="flex flex-wrap justify-start items-start gap-4 pb-6 pt-4 text-neutral-dark">
                        <div className="flex flex-col items-center gap-1 border rounded-2xl p-4">
                          <span className="text-base font-semibold">
                            {t("teamPage.modals.importPreviewTotals.usersReady")}
                          </span>
                          <span
                            className="text-2xl font-bold leading-none"
                          style={{
                            color:
                              readyImportCount === 0
                                ? colors.neutral.medium
                                : colors.successful.medium,
                          }}
                        >
                          {readyImportCount}
                        </span>
                      </div>
                        <div className="flex flex-col items-center gap-1 border rounded-2xl p-4">
                          <span className="text-base font-semibold">{t("teamPage.modals.importPreviewTotals.invalidEntries")}</span>
                          <span
                            className="text-2xl font-bold leading-none"
                            style={{ color: invalidEntriesColor }}
                          >
                            {previewIssuesCount}
                          </span>
                        </div>
                        <div className="flex flex-col items-start gap-0 border rounded-2xl p-4 pb-9 max-w-xs">
                          <div className="w-full" style={{ marginBottom: 15 }}>
                            <div className="text-xs text-neutral-dark mb-1">{t("teamPage.modals.labels.emailLanguage")}</div>
                            <select
                              name="importLanguage"
                              value={importLanguage}
                              onChange={(e) => setImportLanguage(e.target.value as EmailLanguage)}
                              className="w-full h-10 rounded-sm bg-white input-focus-primary text-left px-3 text-sm border border-neutral-light text-neutral-dark"
                            >
                              <option value="en">{t("teamPage.modals.labels.languageEnglish")}</option>
                              <option value="fr">{t("teamPage.modals.labels.languageFrench")}</option>
                            </select>
                          </div>
                          <label
                            htmlFor="importAutoSignUp"
                            className="flex items-center space-x-2 text-sm text-neutral-dark"
                          >
                            <input
                              id="importAutoSignUp"
                              type="checkbox"
                              name="importAutoSignUp"
                              checked={importAutoSignUp}
                              onChange={(e) => setImportAutoSignUp(e.target.checked)}
                              className="h-4 w-4 accent-primary-medium"
                            />
                            <span>{t("teamPage.modals.labels.autoSignUp")}</span>
                          </label>
                          <p className="text-xs text-neutral-dark mt-2">
                            {t("teamPage.modals.labels.autoSignUpHelper")}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-neutral-dark text-right">
                        {t("teamPage.modals.importPreviewTotals.importingOnlyValid")}
                      </div>
                  </div>
                </>
              ) : (
                <div className="py-8 text-center text-sm text-neutral-medium">
                  {t("teamPage.modals.importPreviewEmpty")}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2" style={{ marginTop: -15 }}>
                <ShadcnButton
                  variant="outline"
                  onClick={closeImportPreview}
                  disabled={isParsingImport || isImporting}
                  className="hover:bg-neutral-lightest"
                >
                  {t("teamPage.modals.importActions.cancel")}
                </ShadcnButton>
                <ShadcnButton
                  onClick={onConfirmImportPreview}
                  disabled={
                    isParsingImport || isImporting || readyImportCount === 0
                  }
                  className="bg-primary-medium hover:bg-primary-dark text-white px-16 min-w-[140px]"
                >
                  {isImporting ? t("teamPage.modals.importActions.importing") : t("teamPage.modals.importActions.import")}
                </ShadcnButton>
              </div>
            </div>
          </ReactModal>
        ) : null}
        {errorModalIsOpen ? (
          <ReactModal
            isOpen={errorModalIsOpen}
            onRequestClose={closeErrorModal}
            style={globalStyles.generalModal}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
            contentLabel={t("teamPage.modals.addTitle")}
          >
            <div className="flex flex-col gap-4 p-4 text-center">
              <div className="text-xl font-semibold text-neutral-darkest">
                {errorModalTitle ?? t("teamPage.errors.emailAlreadyExistsGlobalTitle", "Email already in use")}
              </div>
              <div className="text-base text-neutral-dark space-y-3">
                <div>
                  {t(
                    "teamPage.errors.genericImportSubtitle",
                    "The import file appears to be improperly formatted. Please download a template via the Import button menu (either Excel or CSV format)."
                  )}
                </div>
                <div className="flex gap-4 justify-center text-sm font-semibold">
                  <button
                    type="button"
                    className="text-brand-orange hover:underline"
                    onClick={downloadImportTemplateXlsx}
                  >
                    {t("teamPage.import.downloadTemplateXlsx", "Download Excel template")}
                  </button>
                  <button
                    type="button"
                    className="text-brand-orange hover:underline"
                    onClick={downloadImportTemplate}
                  >
                    {t("teamPage.import.downloadTemplate", "Download CSV template")}
                  </button>
                </div>
                <div className="font-semibold py-5">
                  {errorModalMessage ??
                    t(
                      "teamPage.errors.emailAlreadyExistsGlobal",
                      "A user with this email already exists in Coinmiles. Please use a different email address."
                    )}
                </div>
              </div>
              <div className="flex justify-center">
                <Button
                  variant="primary"
                  color="brand-orange"
                  hoverColor="primary-dark"
                  label={t("teamPage.modals.importActions.confirm", "Ok")}
                  onClick={closeErrorModal}
                  textWeight="semibold"
                  stretch
                  size="md"
                />
              </div>
            </div>
          </ReactModal>
        ) : null}
        {isImportResultsOpen ? (
          <ReactModal
            isOpen={isImportResultsOpen}
            onRequestClose={() => setIsImportResultsOpen(false)}
            style={resultsModalStyle}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
            contentLabel={t("teamPage.modals.importResultsTitle", "Team Import Results")}
          >
            <div className="flex flex-col gap-4 p-4">
              <div className="text-2xl font-semibold text-neutral-darkest text-center">
                {t("teamPage.modals.importResultsTitle", "Team Import Results")}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-2">
                  {[
                    { key: "success", label: t("teamPage.importResults.success", "Success"), count: successCount },
                    { key: "duplicate", label: t("teamPage.importResults.duplicate", "Duplicate Email"), count: duplicateCount },
                    { key: "failure", label: t("teamPage.importResults.failure", "General Failure"), count: failureCount },
                  ].map((btn) => {
                    const isActive = importResultsFilter === btn.key;
                    return (
                      <ShadcnButton
                        key={btn.key}
                        variant="outline"
                        className={`px-3 py-2 text-sm font-semibold border bg-white ${
                          isActive
                            ? "text-primary-medium border-primary-medium hover:bg-primary-lightest hover:text-primary-medium"
                            : "text-neutral-dark border-neutral-light"
                        }`}
                        onClick={() => setImportResultsFilter(btn.key as any)}
                      >
                        {btn.label} ({btn.count})
                      </ShadcnButton>
                    );
                  })}
                </div>
              </div>

              <div className="border border-neutral-light rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20 text-left text-xs uppercase tracking-wide">
                        #
                      </TableHead>
                      <TableHead className="text-xs uppercase tracking-wide">
                        {t("teamPage.tableHeaders.name")}
                      </TableHead>
                      <TableHead className="text-xs uppercase tracking-wide">
                        {t("teamPage.tableHeaders.email")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                </Table>
                <div className="h-60 overflow-y-auto">
                  <Table className="h-full">
                    <TableBody className="align-top">
                      {filteredResults.length ? (
                        filteredResults.map((row) => (
                          <TableRow key={`${row.lineNumber}-${row.email}`} className="h-12">
                            <TableCell className="font-mono text-xs text-neutral-medium">
                              {row.lineNumber}
                            </TableCell>
                            <TableCell className="text-sm text-neutral-darkest">
                              {row.firstName} {row.lastName}
                            </TableCell>
                            <TableCell className="text-sm text-neutral-darkest">
                              {row.email}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow className="h-full">
                          <TableCell colSpan={3} className="text-center text-sm text-neutral-medium">
                            {t("teamPage.importResults.empty", "No rows for this filter.")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex flex-wrap justify-between items-center gap-4 pb-2 pt-4 text-neutral-dark">
                <div className="flex flex-wrap justify-start items-stretch gap-4">
                  <div className="flex flex-col items-center gap-1 border rounded-2xl p-4 min-w-[150px]">
                    <span className="text-base font-semibold">
                      {t("teamPage.importResults.successBoxTitle", "Successful Imports")}
                    </span>
                    <span className="text-2xl font-bold leading-none text-green-600">
                      {successCount}
                    </span>
                  </div>
                  <div className="flex flex-col items-center gap-1 border rounded-2xl p-4 min-w-[150px]">
                    <span className="text-base font-semibold">
                      {t("teamPage.importResults.failureBoxTitle", "Failed Imports")}
                    </span>
                  <span className="text-2xl font-bold leading-none" style={{ color: colors.red }}>
                    {duplicateCount + failureCount}
                  </span>
                </div>
                </div>
                {duplicateCount + failureCount > 0 ? (
                  <div className="flex flex-col items-end gap-1 flex-1">
                    <div className="flex flex-col items-center w-72">
                      <ShadcnButton
                        variant="outline"
                        type="button"
                        onClick={exportFailedCsv}
                        className="hover:bg-neutral-lightest w-full"
                      >
                        {t("teamPage.importResults.exportFailures", "Download error report")}
                      </ShadcnButton>
                      <div className="text-xs text-neutral-dark text-right mt-1 leading-snug pr-1">
                        {t("teamPage.importResults.exportDescription", {
                          ext: errorReportExtLabel,
                        })}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="flex justify-end">
                <Button
                  variant="primary"
                  color="brand-orange"
                  hoverColor="primary-dark"
                  label={t("teamPage.modals.importActions.confirm", "Ok")}
                  onClick={() => setIsImportResultsOpen(false)}
                  textWeight="semibold"
                  size="md"
                  className="px-6"
                />
              </div>
            </div>
          </ReactModal>
        ) : null}
        {isImporting ? (
          <ReactModal
            isOpen={isImporting}
            onRequestClose={() => {}}
            style={globalStyles.generalModal}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            ariaHideApp={false}
            contentLabel={t("teamPage.modals.importLoadingTitle")}
          >
            <div className="flex flex-col items-center gap-4 py-6 px-4 text-center">
              <div className="text-xl font-semibold text-neutral-darkest">
                {t("teamPage.modals.importLoadingTitle")}
              </div>
              <div className="text-base text-neutral-dark">
                {importTotalCount > 0
                  ? t("teamPage.modals.importLoadingStatus", {
                      completed: importCompletedCount,
                      total: importTotalCount,
                    })
                  : t("teamPage.modals.importLoadingPreparing")}
              </div>
              <Spinner variant="large" />
              <button
                type="button"
                onClick={onCancelImport}
                className="text-primary-medium text-sm font-semibold hover:underline focus:outline-none"
              >
                {t("teamPage.modals.importActions.cancel")}
              </button>
            </div>
          </ReactModal>
        ) : null}
      </div>
    </Layout>
  );
};
